/**
 * Resolves a single stable identifier for the current visitor, preferring
 * Microsoft Clarity's own id (public/index.html already loads the Clarity
 * tag, project "xmhxshtz0d") so this device/browser can be cross-referenced
 * directly with its Clarity session recording. Falls back to a locally
 * generated, localStorage-persisted id for the (usually brief) window before
 * Clarity has set its cookie, or if Clarity is blocked/unavailable — callers
 * always get *something* stable rather than null.
 */

const FALLBACK_ID_KEY = "mf_user_id";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Clarity's `_clck` cookie holds the stable per-browser Clarity user id as
 * its first segment (distinct from `_clsk`, which is per-session), but the
 * delimiter has changed across Clarity script versions: newer cookies use
 * "^" (e.g. "<id>^2^..."), older ones "|" (e.g. "<id>|2|..."). Try "^" first
 * since that's what current deployments emit — splitting solely on "|" (the
 * old format) leaves the whole raw cookie value intact and sends that as the
 * id instead of just the id segment.
 */
export function getClarityId() {
  const clck = readCookie("_clck");
  if (!clck) return null;
  const delimiter = clck.includes("^") ? "^" : "|";
  const id = clck.split(delimiter)[0];
  return id || null;
}

function getOrCreateFallbackId() {
  try {
    let id = window.localStorage.getItem(FALLBACK_ID_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(FALLBACK_ID_KEY, id);
    }
    return id;
  } catch {
    return "no-local-storage";
  }
}

/**
 * The identifier to use as "the" per-user id everywhere (logs + the
 * `clarityId` field sent to the backend on every API call). Re-reads the
 * cookie on every call (cheap) since Clarity's script loads async and may
 * not have set `_clck` yet on the very first requests of a session — once it
 * does, subsequent calls automatically pick up the real Clarity id instead
 * of the fallback.
 */
export function getUserIdentifier() {
  return getClarityId() || getOrCreateFallbackId();
}
